/**
 * Console 日志劫持模块
 *
 * 功能：
 * 1. 劫持 console.log/warn/error/info/debug 方法
 * 2. 将日志作为 Sentry Breadcrumbs 记录
 * 3. 支持定期批量上传，或在错误发生时自动携带上下文
 * 4. 通过 Sentry 的 user.id / user.username 可以查询用户的完整日志链
 *
 * 使用方式：
 * 在 Sentry 平台搜索: user.id:xxx 或 user.username:xxx
 * 查看 Issue 的 Breadcrumbs 标签页即可看到按时序排列的日志
 */

import * as Sentry from '@sentry/react';

// 日志级别类型
type LogLevel = 'debug' | 'info' | 'log' | 'warning' | 'error';

// 日志条目接口
interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
  data?: Record<string, unknown>;
  count?: number; // Duplicate count
  isCritical?: boolean; // 是否是关键日志
}

// 配置接口
interface ConsoleCaptureConfig {
  // 是否启用日志捕获
  enabled: boolean;
  // 捕获的日志级别
  levels: LogLevel[];
  // 日志缓冲区大小（超过后自动上传）
  bufferSize: number;
  // 自动上传间隔（毫秒），0 表示不自动上传
  autoFlushInterval: number;
  // 日志内容最大长度
  maxMessageLength: number;
  // 是否在控制台保留原始输出
  preserveOriginal: boolean;
  // 过滤函数：返回 false 则不记录该日志
  filter?: (level: LogLevel, args: unknown[]) => boolean;
  // 日志前缀过滤（包含这些前缀的日志不记录）
  excludePrefixes?: string[];
  // 正则表达过滤（匹配正则的日志不记录）
  excludePatterns?: RegExp[];
  // 关键日志前缀（白名单，这些日志始终会被记录并优先上报）
  criticalPrefixes?: string[];
  // error 级别日志的防抖时间（毫秒），避免短时间内大量 error 刷屏
  errorDebounceMs?: number;
  // 是否启用智能上报（只有 error/warning 时才上报）
  smartFlush?: boolean;
  // 是否强制上报所有日志（包括普通 log/info）
  forceFlushAll?: boolean;
}

// 默认配置
const DEFAULT_CONFIG: ConsoleCaptureConfig = {
  enabled: true,
  levels: ['log', 'info', 'warning', 'error', 'debug'], // 启用所有级别
  bufferSize: 100,
  autoFlushInterval: 60000, // 1分钟
  maxMessageLength: 2000,
  preserveOriginal: true,
  excludePrefixes: ['[HMR]', '[vite]', '[webpack]'], // 开发环境噪音过滤
  criticalPrefixes: [], // 关键日志前缀
  errorDebounceMs: 1000, // error 防抖 1 秒
  smartFlush: true, // 默认启用智能上报
  forceFlushAll: false, // 默认不强制上报所有日志
};

// Console 方法到 Sentry 级别的映射
const LEVEL_MAP: Record<string, LogLevel> = {
  log: 'log',
  info: 'info',
  warn: 'warning',
  error: 'error',
  debug: 'debug',
};

// Sentry Breadcrumb 级别映射
const SENTRY_LEVEL_MAP: Record<LogLevel, Sentry.SeverityLevel> = {
  debug: 'debug',
  info: 'info',
  log: 'log',
  warning: 'warning',
  error: 'error',
};

class ConsoleCapture {
  private config: ConsoleCaptureConfig;
  private buffer: LogEntry[] = [];
  private originalMethods: Record<string, (...args: unknown[]) => void> = {};
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isInitialized = false;
  private readonly sessionId: string;
  // error 防抖相关
  private lastErrorFlushTime = 0;
  private pendingErrorFlush: ReturnType<typeof setTimeout> | null = null;
  // 关键日志计数
  private criticalLogCount = 0;

  constructor(config: Partial<ConsoleCaptureConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();
  }

  /**
   * 生成会话 ID，用于关联同一会话的日志
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 初始化 Console 劫持
   */
  init(): void {
    if (this.isInitialized) {
      console.warn('[ConsoleCapture] Already initialized');
      return;
    }

    if (!this.config.enabled) {
      return;
    }

    // 保存原始方法
    this.originalMethods = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console),
    };

    // 劫持各个方法
    this.hijackMethod('log');
    this.hijackMethod('info');
    this.hijackMethod('warn');
    this.hijackMethod('error');
    this.hijackMethod('debug');

    // 设置自动上传定时器
    if (this.config.autoFlushInterval > 0) {
      this.flushTimer = setInterval(() => {
        this.flush('interval');
      }, this.config.autoFlushInterval);
    }

    // 页面卸载时上传剩余日志
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush('beforeunload');
      });

      // 监听页面可见性变化，隐藏时上传
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush('visibilitychange');
        }
      });
    }

    this.isInitialized = true;

    // 记录初始化事件
    Sentry.addBreadcrumb({
      category: 'console-capture',
      message: 'Console capture initialized',
      level: 'info',
      data: {
        sessionId: this.sessionId,
        config: {
          levels: this.config.levels,
          bufferSize: this.config.bufferSize,
          autoFlushInterval: this.config.autoFlushInterval,
        },
      },
    });
  }

  /**
   * 劫持特定的 console 方法
   */
  private hijackMethod(method: string): void {
    const level = LEVEL_MAP[method];
    if (!this.config.levels.includes(level)) {
      return;
    }

    const originalMethod = this.originalMethods[method];
    const self = this;

    // @ts-expect-error - 动态修改 console 方法
    console[method] = function (...args: unknown[]) {
      // 保留原始输出
      if (self.config.preserveOriginal) {
        originalMethod(...args);
      }

      // 捕获日志
      self.captureLog(level, args);
    };
  }

  /**
   * 检查是否是关键日志
   */
  private isCriticalLog(message: string): boolean {
    if (!this.config.criticalPrefixes || this.config.criticalPrefixes.length === 0) {
      return false;
    }
    return this.config.criticalPrefixes.some(prefix => message.includes(prefix));
  }

  /**
   * 捕获单条日志
   */
  private captureLog(level: LogLevel, args: unknown[]): void {
    // 格式化消息（先格式化，后面过滤时要用）
    const message = this.formatMessage(args);

    // 检查是否是关键日志（关键日志不会被过滤）
    const isCritical = this.isCriticalLog(message);

    // 非关键日志才应用过滤器
    if (!isCritical) {
      // 应用自定义过滤器
      if (this.config.filter && !this.config.filter(level, args)) {
        return;
      }

      // 检查前缀过滤（黑名单）
      const firstArg = args[0];
      if (typeof firstArg === 'string' && this.config.excludePrefixes) {
        for (const prefix of this.config.excludePrefixes) {
          if (firstArg.startsWith(prefix)) {
            return;
          }
        }
      }

      // 检查正则过滤（黑名单）
      if (this.config.excludePatterns) {
        for (const pattern of this.config.excludePatterns) {
          if (pattern.test(message)) {
            return;
          }
        }
      }
    }

    const timestamp = Date.now();

    // 简单的去重逻辑：如果与上一条日志的消息和级别相同，则只增加计数
    // 但关键日志不去重，每条都记录
    const lastEntry = this.buffer[this.buffer.length - 1];
    if (
      !isCritical &&
      lastEntry &&
      lastEntry.message === message &&
      lastEntry.level === level
    ) {
      lastEntry.count = (lastEntry.count || 1) + 1;
      lastEntry.timestamp = timestamp; // 更新时间为最后一次出现的时间
      return;
    }

    // 创建日志条目
    const entry: LogEntry = {
      level,
      message,
      timestamp,
      data: this.extractData(args),
      count: 1,
      isCritical,
    };

    // 添加到缓冲区
    this.buffer.push(entry);

    // 如果是关键日志，增加计数
    if (isCritical) {
      this.criticalLogCount++;
    }

    // 添加 Sentry Breadcrumb（这是关键，让日志与后续的 error 关联）
    Sentry.addBreadcrumb({
      category: isCritical ? 'console.critical' : 'console',
      message: message.substring(0, this.config.maxMessageLength),
      level: SENTRY_LEVEL_MAP[level],
      timestamp: timestamp / 1000, // Sentry 使用秒
      data: {
        ...entry.data,
        isCritical,
      },
    });

    // 如果是 error 级别，使用防抖上传（避免短时间内大量 error 刷屏）
    if (level === 'error') {
      this.debouncedErrorFlush();
    }

    // 如果是关键日志且积累了一定数量，主动上报
    if (isCritical && this.criticalLogCount >= 5) {
      this.flush('critical-logs');
      this.criticalLogCount = 0;
    }

    // 检查缓冲区大小
    if (this.buffer.length >= this.config.bufferSize) {
      this.flush('buffer-full');
    }
  }

  /**
   * 防抖的 error flush，避免短时间内大量 error 刷屏
   */
  private debouncedErrorFlush(): void {
    const now = Date.now();
    const debounceMs = this.config.errorDebounceMs || 1000;

    // 如果距离上次 error flush 时间不够长，设置延迟
    if (now - this.lastErrorFlushTime < debounceMs) {
      // 清除之前的延迟
      if (this.pendingErrorFlush) {
        clearTimeout(this.pendingErrorFlush);
      }
      // 设置新的延迟
      this.pendingErrorFlush = setTimeout(() => {
        this.flush('error');
        this.lastErrorFlushTime = Date.now();
        this.pendingErrorFlush = null;
      }, debounceMs);
    } else {
      // 立即 flush
      this.flush('error');
      this.lastErrorFlushTime = now;
    }
  }

  /**
   * 格式化日志消息
   */
  private formatMessage(args: unknown[]): string {
    return args
      .map(arg => {
        if (arg === null) return 'null';
        if (arg === undefined) return 'undefined';
        if (typeof arg === 'string') return arg;
        if (typeof arg === 'number' || typeof arg === 'boolean') {
          return String(arg);
        }
        if (arg instanceof Error) {
          return `${arg.name}: ${arg.message}`;
        }
        try {
          return JSON.stringify(arg, this.jsonReplacer, 0);
        } catch {
          return String(arg);
        }
      })
      .join(' ')
      .substring(0, this.config.maxMessageLength);
  }

  /**
   * JSON 序列化替换函数，处理循环引用等
   */
  private jsonReplacer(_key: string, value: unknown): unknown {
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }
    if (typeof value === 'function') {
      return '[Function]';
    }
    if (value instanceof HTMLElement) {
      return `[HTMLElement: ${value.tagName}]`;
    }
    return value;
  }

  /**
   * 提取日志中的结构化数据
   */
  private extractData(args: unknown[]): Record<string, unknown> | undefined {
    const data: Record<string, unknown> = {};

    args.forEach((arg, index) => {
      if (arg !== null && typeof arg === 'object' && !(arg instanceof Error)) {
        try {
          // 只提取简单对象
          const keys = Object.keys(arg as object);
          if (keys.length <= 10) {
            data[`arg${index}`] = arg;
          }
        } catch {
          // 忽略无法序列化的对象
        }
      }
    });

    return Object.keys(data).length > 0 ? data : undefined;
  }

  /**
   * 上传日志到 Sentry
   */
  flush(
    trigger:
      | 'interval'
      | 'error'
      | 'buffer-full'
      | 'manual'
      | 'beforeunload'
      | 'visibilitychange'
      | 'critical-logs' = 'manual'
  ): void {
    if (this.buffer.length === 0) {
      return;
    }

    const logs = [...this.buffer];
    this.buffer = [];

    // 统计日志类型
    const hasError = logs.some(log => log.level === 'error');
    const hasWarning = logs.some(log => log.level === 'warning');
    const hasCritical = logs.some(log => log.isCritical);
    const criticalLogs = logs.filter(log => log.isCritical);

    const isAutoTrigger =
      trigger === 'interval' ||
      trigger === 'buffer-full' ||
      trigger === 'visibilitychange';

    // 智能上传策略（可配置）
    // 如果是自动触发，且没有 Error/Warning/Critical 级别的日志，则不上报
    const shouldSkip =
      this.config.smartFlush &&
      !this.config.forceFlushAll &&
      isAutoTrigger &&
      !hasError &&
      !hasWarning &&
      !hasCritical;

    if (shouldSkip) {
      // 只有普通日志，且是自动触发 -> 忽略上传，只作为本地 Breadcrumbs 历史
      return;
    }

    // 创建日志摘要
    const summary = {
      sessionId: this.sessionId,
      trigger,
      logCount: logs.length,
      criticalLogCount: criticalLogs.length,
      levels: this.countByLevel(logs),
      timeRange: {
        start: logs[0]?.timestamp,
        end: logs[logs.length - 1]?.timestamp,
      },
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    };

    // 发送到 Sentry（作为 message 类型的事件）
    Sentry.withScope(scope => {
      // 设置额外上下文
      scope.setContext('console_logs', {
        summary,
        // 优先展示关键日志
        criticalLogs: criticalLogs.map(log => ({
          level: log.level,
          message: log.message.substring(0, 1000),
          timestamp: new Date(log.timestamp).toISOString(),
        })),
        // 上报所有缓冲区内的日志 (最大 bufferSize)
        recentLogs: logs.map(log => {
          let msg = log.message.substring(0, 1000);
          if (log.count && log.count > 1) {
            msg = `[x${log.count}] ${msg}`;
          }
          return {
            level: log.level,
            message: msg,
            timestamp: new Date(log.timestamp).toISOString(),
            isCritical: log.isCritical,
          };
        }),
      });

      scope.setTag('console_capture_trigger', trigger);
      scope.setTag('session_id', this.sessionId);
      scope.setTag('has_critical_logs', String(hasCritical));

      // 设置级别（如果有 error 则设为 error）
      const eventLevel = hasError ? 'error' : hasWarning ? 'warning' : 'info';
      scope.setLevel(eventLevel);

      // 生成更有意义的消息
      let messagePrefix = '[Console Logs]';
      if (hasCritical) {
        messagePrefix = '[Critical Console Logs]';
      }
      const levelInfo = hasError
        ? 'with errors'
        : hasWarning
          ? 'with warnings'
          : hasCritical
            ? 'with critical info'
            : 'info only';

      // 发送消息
      Sentry.captureMessage(
        `${messagePrefix} ${logs.length} logs (${trigger}, ${levelInfo})`,
        eventLevel
      );
    });
  }

  /**
   * 统计各级别日志数量
   */
  private countByLevel(logs: LogEntry[]): Record<string, number> {
    return logs.reduce(
      (acc, log) => {
        acc[log.level] = (acc[log.level] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }

  /**
   * 手动记录日志（不经过 console）
   */
  capture(
    level: LogLevel,
    message: string,
    data?: Record<string, unknown>
  ): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      data,
      count: 1,
    };

    this.buffer.push(entry);

    Sentry.addBreadcrumb({
      category: 'console',
      message,
      level: SENTRY_LEVEL_MAP[level],
      data,
    });
  }

  /**
   * 获取当前缓冲区内容（调试用）
   */
  getBuffer(): LogEntry[] {
    return [...this.buffer];
  }

  /**
   * 清空缓冲区
   */
  clearBuffer(): void {
    this.buffer = [];
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<ConsoleCaptureConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 销毁实例，恢复原始 console 方法
   */
  destroy(): void {
    if (!this.isInitialized) {
      return;
    }

    // 上传剩余日志
    this.flush('manual');

    // 恢复原始方法
    Object.entries(this.originalMethods).forEach(([method, fn]) => {
      // @ts-expect-error - 恢复 console 方法
      console[method] = fn;
    });

    // 清除定时器
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // 清除 error 防抖定时器
    if (this.pendingErrorFlush) {
      clearTimeout(this.pendingErrorFlush);
      this.pendingErrorFlush = null;
    }

    this.isInitialized = false;
  }

  /**
   * 获取会话 ID
   */
  getSessionId(): string {
    return this.sessionId;
  }
}

// 单例实例
let consoleCaptureInstance: ConsoleCapture | null = null;

/**
 * 初始化 Console 日志捕获
 */
export function initConsoleCapture(
  config: Partial<ConsoleCaptureConfig> = {}
): ConsoleCapture {
  if (consoleCaptureInstance) {
    return consoleCaptureInstance;
  }

  consoleCaptureInstance = new ConsoleCapture(config);
  consoleCaptureInstance.init();

  return consoleCaptureInstance;
}

/**
 * 获取 Console 捕获实例
 */
export function getConsoleCapture(): ConsoleCapture | null {
  return consoleCaptureInstance;
}

/**
 * 手动上传日志
 */
export function flushConsoleLogs(): void {
  consoleCaptureInstance?.flush('manual');
}

/**
 * 销毁 Console 捕获
 */
export function destroyConsoleCapture(): void {
  consoleCaptureInstance?.destroy();
  consoleCaptureInstance = null;
}

export {
  ConsoleCapture,
  type ConsoleCaptureConfig,
  type LogEntry,
  type LogLevel,
};
